import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { catalogService } from '@/data/services/catalogService';
import { queryKeys } from '@/lib/query-keys';
import type { FieldValue } from '@/data/schemas/fieldPatterns.schemas';
import { useIsSafeMode } from '@/lib/safeMode';
import { inputVariants } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface CatalogInputProps {
  catalogId: string;
  value?: FieldValue;
  onValueChange?: (value?: FieldValue) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
}

export function ClosedEnumSelect({
  catalogId,
  value,
  onValueChange,
  placeholder = "Seleziona un'opzione",
  disabled = false,
  required = false,
  id,
}: CatalogInputProps): JSX.Element {
  const unsetValue = '__unset__';
  const isSafeMode = useIsSafeMode();

  const { data: options = [], isLoading } = useQuery({
    queryKey: queryKeys.catalogs.options(catalogId),
    queryFn: () => catalogService.getCatalogOptions(catalogId, { includeInactive: true }),
  });

  const resolvedValue = value?.kind === 'option' ? value.optionId : !required ? unsetValue : '';

  const handleValueChange = (nextValue: string) => {
    if (!required && nextValue === unsetValue) {
      onValueChange?.(undefined);
      return;
    }
    onValueChange?.({ kind: 'option', optionId: nextValue });
  };

  // SAFE MODE: Use native select for better compatibility
  if (isSafeMode) {
    return (
      <select
        id={id}
        value={resolvedValue}
        onChange={(e) => handleValueChange(e.target.value)}
        disabled={disabled || isLoading}
        required={required}
        className={cn(inputVariants(), 'pr-8')}
      >
        {!required && <option value={unsetValue}>Non specificato</option>}
        {isLoading && <option value="">Caricamento...</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id} disabled={option.isActive === false}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <Select
      value={resolvedValue}
      onValueChange={handleValueChange}
      disabled={disabled || isLoading}
      required={required}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder={isLoading ? 'Caricamento...' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {!required && (
          <SelectItem value={unsetValue}>
            <em>Non specificato</em>
          </SelectItem>
        )}
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id} disabled={option.isActive === false}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
